import * as Api from '@ViewModels';

/**
 * Validation utility functions for automation steps.
 * These replace the ViewModel computed properties like `hasAllRequiredInfo`.
 */

/**
 * Validates if an email automation step has all required information.
 */
export function validateEmailStep(step: Api.IEmailAutomationStep): boolean {
	if (!step) {
		return false;
	}

	const hasSubject = !!step.options?.subject;
	const hasContent = !!step.content;

	return hasSubject && hasContent;
}

/**
 * Validates if a texting automation step has all required information.
 */
export function validateTextingStep(step: Api.ITextingAutomationStep): boolean {
	if (!step) {
		return false;
	}

	const hasContent = !!step.content;
	return hasContent;
}

/**
 * Validates if a tag automation step has all required information.
 */
export function validateTagStep(step: Api.ITagAutomationStep): boolean {
	if (!step) {
		return false;
	}

	const hasTagName = !!step.tagName;
	return hasTagName;
}

/**
 * Validates if an action item automation step has all required information.
 */
export function validateActionItemStep(step: Api.IActionItemAutomationStep): boolean {
	if (!step) {
		return false;
	}

	const hasContent = !!step.content;
	return hasContent;
}

/**
 * Validates if a handwritten card automation step has all required information.
 */
export function validateHandwrittenCardStep(step: Api.IAutomationStep): boolean {
	if (!step) {
		return false;
	}

	// Handwritten card steps use the base IAutomationStep interface
	const hasContent = !!step.content;
	return hasContent;
}

/**
 * Validates if a newsletter automation step has all required information.
 */
export function validateNewsletterStep(step: Api.IHtmlNewsletterAutomationStep): boolean {
	if (!step) {
		return false;
	}

	const hasContent = !!step.content;
	return hasContent;
}

/**
 * Validates if an opportunity automation step has all required information.
 */
export function validateOpportunityStep(_step: Api.IAutomationStep): boolean {
	// Opportunity steps use the base IAutomationStep interface and don't have required fields
	return true;
}

/**
 * Validates if a switch automation step has all required information.
 */
export function validateSwitchStep(step: Api.ISwitchAutomationStep): boolean {
	if (!step) {
		return false;
	}

	// A switch step needs at least one case with a template ID
	const hasCases = !!step.cases && step.cases.length > 0;
	const hasValidCases =
		hasCases &&
		step.cases.some((caseStatement: Api.IAutomationStepCaseStatement) => !!caseStatement.automationTemplateId);

	return hasValidCases;
}

/**
 * Generic validation function that validates any automation step based on its type.
 */
export function validateAutomationStep(step: Api.IAutomationStep): boolean {
	if (!step || !step._type) {
		return false;
	}

	const stepType = step._type as string;

	// Handle both short format (e.g., "Email") and full format (e.g., "EmailAutomationStep")
	switch (stepType) {
		case Api.AutomationStepType.Email:
		case 'EmailAutomationStep':
			return validateEmailStep(step as unknown as Api.IEmailAutomationStep);
		case Api.AutomationStepType.Texting:
		case 'TextingAutomationStep':
			return validateTextingStep(step as unknown as Api.ITextingAutomationStep);
		case Api.AutomationStepType.AddTag:
		case 'AddTagAutomationStep':
		case Api.AutomationStepType.RemoveTag:
		case 'RemoveTagAutomationStep':
			return validateTagStep(step as unknown as Api.ITagAutomationStep);
		case Api.AutomationStepType.ActionItem:
		case 'ActionItemAutomationStep':
			return validateActionItemStep(step as unknown as Api.IActionItemAutomationStep);
		case Api.AutomationStepType.HandwrittenCard:
		case 'HandwrittenCardAutomationStep':
			return validateHandwrittenCardStep(step);
		case Api.AutomationStepType.HtmlNewsletter:
		case 'HtmlNewsletterAutomationStep':
			return validateNewsletterStep(step as unknown as Api.IHtmlNewsletterAutomationStep);
		case Api.AutomationStepType.Opportunity:
		case 'OpportunityAutomationStep':
			return validateOpportunityStep(step);
		case Api.AutomationStepType.Switch:
		case 'SwitchAutomationStep':
			return validateSwitchStep(step as unknown as Api.ISwitchAutomationStep);
		case Api.AutomationStepType.NoAction:
		case 'NoActionAutomationStep':
			return true; // NoAction steps have no required fields
		default:
			return false;
	}
}
